import { createApp, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { useSizeChart } from '~/composables/use-size-chart';

import { BaseSection } from './base-section'; // Assuming BaseSection is relevant or can be omitted if not

/**
 * Product Size Chart section handler
 */
class ProductSizeChartSection extends BaseSection {
  /**
   * Initialize the product size chart
   */
  constructor() {
    super('[data-section-type="product-size-chart"]');
  }

  /**
   * Initialize the section
   */
  init(instanceData) {
    const container = instanceData.container;

    createApp({
      name: 'ProductSizeChartManager',
      setup() {
        const { closeSizeChart, isSizeChartOpen } = useSizeChart();

        const tableContainerRef = ref(null);
        const scrollIndicatorRef = ref(null);
        const scrollTrackRef = ref(null);

        const isDragging = ref(false);
        const dragStartX = ref(0);
        const dragInitialIndicatorLeft = ref(0);
        const isScrollbarVisible = ref(false);

        const refreshScrollbarMetrics = () => {
          if (!tableContainerRef.value || !scrollIndicatorRef.value || !scrollTrackRef.value) {
            isScrollbarVisible.value = false;
            return;
          }

          const table = tableContainerRef.value;
          const indicator = scrollIndicatorRef.value;
          const track = scrollTrackRef.value;

          if (table.offsetParent === null || track.offsetParent === null) {
            isScrollbarVisible.value = false;
            return;
          }

          const tableClientWidth = table.clientWidth;
          const tableScrollWidth = table.scrollWidth;

          if (
            tableScrollWidth <= tableClientWidth ||
            tableClientWidth === 0 ||
            tableScrollWidth === 0
          ) {
            isScrollbarVisible.value = false;
            if (indicator) {
              indicator.style.width = '0px';
              indicator.style.left = '0px';
            }
            return;
          }

          isScrollbarVisible.value = true;

          const trackWidth = track.clientWidth;
          if (trackWidth > 0 && tableScrollWidth > 0) {
            const indicatorWidthRatio = tableClientWidth / tableScrollWidth;
            const newIndicatorWidth = Math.max(trackWidth * indicatorWidthRatio, 20); // Min width 20px
            if (indicator) indicator.style.width = `${newIndicatorWidth}px`;
          } else if (indicator) {
            indicator.style.width = '0px'; // Fallback
          }

          updateIndicatorPosition();
        };

        const updateIndicatorPosition = () => {
          if (
            !isScrollbarVisible.value ||
            !tableContainerRef.value ||
            !scrollIndicatorRef.value ||
            !scrollTrackRef.value
          ) {
            return;
          }

          const table = tableContainerRef.value;
          const indicator = scrollIndicatorRef.value;
          const track = scrollTrackRef.value;

          if (
            table.offsetParent === null ||
            indicator.offsetParent === null ||
            track.offsetParent === null
          ) {
            return;
          }

          const tableScrollableWidth = table.scrollWidth - table.clientWidth;
          const trackWidth = track.clientWidth;
          const currentIndicatorWidth = indicator.offsetWidth;
          const indicatorTradableWidth = trackWidth - currentIndicatorWidth;

          if (tableScrollableWidth > 0 && indicatorTradableWidth >= 0) {
            const scrollPercentage = table.scrollLeft / tableScrollableWidth;
            const newLeft = Math.max(
              0,
              Math.min(scrollPercentage * indicatorTradableWidth, indicatorTradableWidth),
            );

            indicator.style.left = `${newLeft}px`;
          } else {
            indicator.style.left = '0px';
          }
        };

        const handleTableScroll = () => {
          updateIndicatorPosition();
        };

        /**
         * Updates the table's scroll position based on the new position of the scrollbar indicator.
         * @param {number} newIndicatorLeft - The new left position of the indicator.
         */
        const updateTableScrollOnIndicatorDrag = (newIndicatorLeft = 0) => {
          if (
            !isScrollbarVisible.value ||
            !tableContainerRef.value ||
            !scrollIndicatorRef.value ||
            !scrollTrackRef.value
          ) {
            return;
          }

          const table = tableContainerRef.value;
          const indicator = scrollIndicatorRef.value;
          const track = scrollTrackRef.value;

          const tableScrollableWidth = table.scrollWidth - table.clientWidth;
          const trackVisibleWidth = track.clientWidth;
          const currentIndicatorWidth = indicator.offsetWidth;
          const indicatorTradableWidth = trackVisibleWidth - currentIndicatorWidth;

          if (indicatorTradableWidth >= 0 && tableScrollableWidth > 0) {
            const clampedIndicatorLeft = Math.max(
              0,
              Math.min(newIndicatorLeft, indicatorTradableWidth),
            );
            const scrollPercentage =
              indicatorTradableWidth > 0 ? clampedIndicatorLeft / indicatorTradableWidth : 0;
            table.scrollLeft = scrollPercentage * tableScrollableWidth;
          } else if (tableScrollableWidth <= 0) {
            table.scrollLeft = 0; // Ensure table is at start if not scrollable
          }
        };

        /**
         * Handles the mousedown event on the scrollbar indicator.
         * @param {MouseEvent} event - The mousedown event.
         */
        const handleIndicatorMouseDown = (event) => {
          if (!scrollIndicatorRef.value || !tableContainerRef.value || !isScrollbarVisible.value) {
            return;
          }
          event.preventDefault(); // Prevent text selection during drag
          isDragging.value = true;
          dragStartX.value = event.clientX;
          dragInitialIndicatorLeft.value = scrollIndicatorRef.value.offsetLeft;

          window.addEventListener('mousemove', handleDocumentMouseMove);
          window.addEventListener('mouseup', handleDocumentMouseUp);
        };

        /**
         * Handles the mousemove event on the document when dragging the indicator.
         * @param {MouseEvent} event - The mousemove event.
         */
        const handleDocumentMouseMove = (event) => {
          if (!isDragging.value) return;
          const deltaX = event.clientX - dragStartX.value;
          const newIndicatorLeft = dragInitialIndicatorLeft.value + deltaX;
          updateTableScrollOnIndicatorDrag(newIndicatorLeft);
        };

        /**
         * Handles the mouseup event on the document to end dragging.
         */
        const handleDocumentMouseUp = () => {
          if (!isDragging.value) return;
          isDragging.value = false;
          window.removeEventListener('mousemove', handleDocumentMouseMove);
          window.removeEventListener('mouseup', handleDocumentMouseUp);
        };

        /**
         * Handles the touchstart event on the scrollbar indicator.
         * @param {TouchEvent} event - The touchstart event.
         */
        const handleIndicatorTouchStart = (event) => {
          if (!scrollIndicatorRef.value || !tableContainerRef.value || !isScrollbarVisible.value) {
            return;
          }
          isDragging.value = true;
          dragStartX.value = event.touches[0].clientX;
          dragInitialIndicatorLeft.value = scrollIndicatorRef.value.offsetLeft;

          window.addEventListener('touchmove', handleDocumentTouchMove, { passive: false });
          window.addEventListener('touchend', handleDocumentTouchEnd);
        };

        /**
         * Handles the touchmove event on the document when dragging the indicator.
         * @param {TouchEvent} event - The touchmove event.
         */
        const handleDocumentTouchMove = (event) => {
          if (!isDragging.value) return;
          event.preventDefault();
          const deltaX = event.touches[0].clientX - dragStartX.value;
          const newIndicatorLeft = dragInitialIndicatorLeft.value + deltaX;
          updateTableScrollOnIndicatorDrag(newIndicatorLeft);
        };

        /**
         * Handles the touchend event on the document to end dragging.
         */
        const handleDocumentTouchEnd = () => {
          if (!isDragging.value) return;
          isDragging.value = false;
          window.removeEventListener('touchmove', handleDocumentTouchMove);
          window.removeEventListener('touchend', handleDocumentTouchEnd);
        };

        const handleEscKey = (event) => {
          if (event.key === 'Escape' && isSizeChartOpen.value) {
            closeSizeChart();
          }
        };

        const handleResize = () => {
          if (isSizeChartOpen.value) {
            refreshScrollbarMetrics();
          }
        };

        onMounted(() => {
          window.addEventListener('keydown', handleEscKey);
          window.addEventListener('resize', handleResize);

          if (tableContainerRef.value) {
            tableContainerRef.value.addEventListener('scroll', handleTableScroll);
            if (isSizeChartOpen.value) {
              nextTick(() => {
                setTimeout(() => {
                  refreshScrollbarMetrics();
                }, 0);
              });
            }
          }
        });

        watch(isSizeChartOpen, (newValue) => {
          if (newValue) {
            nextTick(() => {
              setTimeout(() => {
                refreshScrollbarMetrics();
                tableContainerRef.value.scrollLeft = 0;
              }, 0);
            });
          } else {
            isScrollbarVisible.value = false;
          }
        });

        onBeforeUnmount(() => {
          window.removeEventListener('keydown', handleEscKey);
          window.removeEventListener('resize', handleResize);
          if (tableContainerRef.value) {
            tableContainerRef.value.removeEventListener('scroll', handleTableScroll);
          }
          window.removeEventListener('mousemove', handleDocumentMouseMove);
          window.removeEventListener('mouseup', handleDocumentMouseUp);
          window.removeEventListener('touchmove', handleDocumentTouchMove);
          window.removeEventListener('touchend', handleDocumentTouchEnd);
        });

        return {
          closeSizeChart,
          handleIndicatorMouseDown,
          handleIndicatorTouchStart,
          isScrollbarVisible,
          isSizeChartOpen,
          scrollIndicatorRef,
          scrollTrackRef,
          tableContainerRef,
        };
      },
    }).mount(container);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ProductSizeChartSection();
});
